/** @format */

import React from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import StackedLineChartRoundedIcon from "@mui/icons-material/StackedLineChartRounded";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import useResponsive from "../auth/useResponsive";
import resolveVisualization from "./resolveVisualization";
import BigStat from "./BigStat";

const COLORS = [
  "#6366f1",
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#ec4899",
];

// Session-wide remembered preference for the chart view. Applied to a new
// chart only when it's valid for that chart's data shape.
const VIEW_PREF_KEY = "propilot_chart_view";

const readViewPref = () => {
  try {
    return window.localStorage.getItem(VIEW_PREF_KEY) || null;
  } catch (e) {
    return null;
  }
};
const writeViewPref = (v) => {
  try {
    window.localStorage.setItem(VIEW_PREF_KEY, v);
  } catch (e) {
    /* localStorage unavailable — in-memory only */
  }
};

const formatNum = (v) => {
  if (v == null || v === "") return v;
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const truncate = (s, n = 14) => {
  const str = String(s ?? "");
  return str.length > n ? str.slice(0, n) + "…" : str;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        boxShadow: "0 4px 6px -1px rgba(15,23,42,0.1)",
        p: 1,
        fontSize: 12,
      }}
    >
      {label != null && (
        <Box sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}>
          {String(label)}
        </Box>
      )}
      {payload.map((entry, i) => (
        <Box
          key={i}
          sx={{ display: "flex", alignItems: "center", gap: 1, color: "#475569" }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: entry.color,
            }}
          />
          <Box>
            {entry.name}:{" "}
            <Box component="strong" sx={{ color: "#0f172a" }}>
              {formatNum(entry.value)}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Compact side-by-side mini-bars for 2–3 categorical points — a full axis
// chart is overkill at that size (see resolveVisualization.js).
const ComparisonBars = ({ data, xKey, yKey, title }) => {
  const values = data.map((row) => {
    const n = Number(row?.[yKey]);
    return Number.isFinite(n) ? n : 0;
  });
  const max = Math.max(...values, 0);
  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1,
        p: 1.5,
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#fafbff",
      }}
    >
      {title && (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            mb: 1,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}>
        {data.map((row, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                width: { xs: 88, sm: 120 },
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={String(row?.[xKey] ?? "")}
            >
              {String(row?.[xKey] ?? "")}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  height: 16,
                  borderRadius: "4px",
                  backgroundColor: COLORS[i % COLORS.length],
                  width: max > 0 ? `${Math.max((values[i] / max) * 100, 2)}%` : "2%",
                  transition: "width 300ms ease",
                }}
              />
            </Box>
            <Typography
              sx={{
                flexShrink: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "#0f172a",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatNum(row?.[yKey])}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Compact data-table rendering of the chart's underlying rows.
const DataTableView = ({ data }) => {
  const columns = Object.keys(data[0] || {});
  const MAX = 200;
  const rows = data.slice(0, MAX);
  return (
    <Box
      sx={{
        maxHeight: 280,
        overflow: "auto",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
      }}
    >
      <Box
        component="table"
        sx={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
      >
        <Box component="thead" sx={{ position: "sticky", top: 0, zIndex: 1 }}>
          <Box component="tr">
            {columns.map((c) => (
              <Box
                component="th"
                key={c}
                sx={{
                  textAlign: "left",
                  px: 1.25,
                  py: 0.75,
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  color: "#475569",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {c}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, ri) => (
            <Box
              component="tr"
              key={ri}
              sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafbfc" } }}
            >
              {columns.map((c) => (
                <Box
                  component="td"
                  key={c}
                  sx={{
                    px: 1.25,
                    py: 0.6,
                    borderBottom: "1px solid #f1f5f9",
                    color: "#334155",
                    whiteSpace: "nowrap",
                  }}
                >
                  {typeof row[c] === "number"
                    ? formatNum(row[c])
                    : String(row[c] ?? "")}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
      {data.length > MAX && (
        <Box sx={{ px: 1.25, py: 0.75, fontSize: 11, color: "#94a3b8" }}>
          Showing first {MAX} of {data.length.toLocaleString()} rows.
        </Box>
      )}
    </Box>
  );
};

const ChartView = ({ chart }) => {
  const isMobile = useResponsive("down", "md");

  // Decide the honest presentation for this data shape — a single point gets
  // a BigStat, 2–3 categories get compact comparison bars, real series get
  // the full chart with only the views that make sense for the shape.
  const resolution = React.useMemo(() => resolveVisualization(chart), [chart]);
  const hasData = resolution.mode !== "none";
  const { type, data, name_key, value_key, title } = chart || {};
  const { xKey, yKeys } = resolution;

  // Which views are valid for this data shape.
  const options = React.useMemo(() => {
    if (!hasData) return [];
    const out = [];
    if (yKeys.length >= 1) {
      out.push({ key: "bar", label: "Bar", icon: <BarChartRoundedIcon /> });
      out.push({ key: "line", label: "Line", icon: <ShowChartRoundedIcon /> });
      out.push({ key: "area", label: "Area", icon: <StackedLineChartRoundedIcon /> });
      if (resolution.allowPie) {
        out.push({ key: "pie", label: "Pie", icon: <PieChartRoundedIcon /> });
      }
    }
    out.push({ key: "table", label: "Table", icon: <TableChartRoundedIcon /> });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, resolution]);

  const backendView = resolution.recommendedChart || "table";

  const validKeys = options.map((o) => o.key);
  const initialView = React.useMemo(() => {
    const pref = readViewPref();
    if (pref && validKeys.includes(pref)) return pref;
    if (validKeys.includes(backendView)) return backendView;
    return validKeys[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendView]);

  const [view, setView] = React.useState(initialView);

  // Keep `view` valid if the chart instance changes underneath us.
  React.useEffect(() => {
    if (!validKeys.includes(view)) setView(initialView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialView]);

  if (!hasData) return null;

  if (resolution.mode === "bigstat") {
    return <BigStat {...resolution.bigStat} />;
  }

  if (resolution.mode === "comparison") {
    return (
      <ComparisonBars data={data} xKey={xKey} yKey={yKeys[0]} title={title} />
    );
  }

  // Crowded legends eat half the chart on a phone — drop them there.
  const showLegend = !isMobile || yKeys.length <= 2;
  const chartHeight = isMobile ? 220 : 280;

  const handleViewChange = (_e, next) => {
    if (!next) return; // ignore deselect — keep one option always active
    setView(next);
    writeViewPref(next);
  };

  const renderChart = () => {
    switch (view) {
      case "pie":
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey={value_key || yKeys[0] || "value"}
              nameKey={name_key || xKey}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        );
      case "line":
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={xKey} tickFormatter={(v) => truncate(v)} fontSize={11} stroke="#94a3b8" />
            <YAxis tickFormatter={formatNum} fontSize={11} stroke="#94a3b8" />
            <RechartsTooltip content={<CustomTooltip />} />
            {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {yKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={COLORS[i % COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
          </LineChart>
        );
      case "area":
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={xKey} tickFormatter={(v) => truncate(v)} fontSize={11} stroke="#94a3b8" />
            <YAxis tickFormatter={formatNum} fontSize={11} stroke="#94a3b8" />
            <RechartsTooltip content={<CustomTooltip />} />
            {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {yKeys.map((k, i) => (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                stroke={COLORS[i % COLORS.length]}
                fill={COLORS[i % COLORS.length]}
                fillOpacity={0.3}
              />
            ))}
          </AreaChart>
        );
      case "bar":
      default:
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={xKey} tickFormatter={(v) => truncate(v)} fontSize={11} stroke="#94a3b8" />
            <YAxis tickFormatter={formatNum} fontSize={11} stroke="#94a3b8" />
            <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: "rgba(99,102,241,0.06)" }} />
            {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {yKeys.map((k, i) => (
              <Bar
                key={k}
                dataKey={k}
                fill={COLORS[i % COLORS.length]}
                stackId={type === "stacked_bar" ? "a" : undefined}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        );
    }
  };

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        {title ? (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {title}
          </Typography>
        ) : (
          <Box />
        )}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={handleViewChange}
          sx={{
            "& .MuiToggleButton-root": {
              px: 1,
              py: 0.4,
              border: "1px solid #e2e8f0",
              color: "#64748b",
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 600,
              "&.Mui-selected": {
                backgroundColor: "#eef2ff",
                color: "#4338ca",
                borderColor: "#c7d2fe",
                "&:hover": { backgroundColor: "#e0e7ff" },
              },
            },
          }}
        >
          {options.map((o) => (
            <ToggleButton key={o.key} value={o.key} aria-label={o.label}>
              <Tooltip title={o.label} arrow>
                {React.cloneElement(o.icon, { sx: { fontSize: 16 } })}
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {view === "table" ? (
        <DataTableView data={data} />
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          {renderChart()}
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default ChartView;
